import {
  ErrorView,
  getValueByPath,
  getWorkTaskId,
  IStep,
  PageLayout,
  PageNames,
  useAppStore,
  useLocationTrackingForWork,
  useLockContextStore,
  useWorkPageStore,
  useWorkScroll,
  useWorkStepEditingStore,
  WorkLayoutRenderingMode,
  useWorkData,
} from '@diana/core';
import { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useWorkPageLeftDrawerProps, useWorkPageRightDrawerProps } from './useWorkPageDrawerProps';
import { WorkPageContent } from './WorkPageContent';
import { WorkPageHeader } from './WorkPageHeader';

export const WorkPage = () => {
  const { workId } = useParams<{ workId: string }>();

  const work = useWorkPageStore((store) => store.work);
  const setWork = useWorkPageStore((store) => store.setWork);
  const setFlow = useWorkPageStore((store) => store.setFlow);
  const resetWorkPageStore = useWorkPageStore((store) => store.reset);
  const renderingMode = useWorkPageStore((store) => store.renderingMode);
  const setRenderingMode = useWorkPageStore((store) => store.setRenderingMode);
  const setActiveTaskId = useWorkPageStore((store) => store.setActiveTaskId);
  const setSelectedWorkId = useAppStore((store) => store.setSelectedWorkId);
  const setLastVisitedWorkId = useAppStore((store) => store.setLastVisitedWorkId);
  const selectedWorkId = useAppStore((store) => store.selectedWorkId);
  const setLockContext = useLockContextStore((ctx) => ctx.setLockContext);
  const hasEditingSteps = useWorkStepEditingStore((store) => store.hasEditingSteps);
  const isEditingSteps = hasEditingSteps();

  const { track } = useLocationTrackingForWork({ work: work || undefined });
  useEffect(() => {
    if (work) track();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [work?.id]);

  const workQueryEnabled = Boolean(workId);
  const hasSetWork = useRef(false);

  const {
    work: data,
    flow,
    error: workError,
    isLoading: _isWorkLoading,
    isFetching: isWorkFetching,
    isSuccess,
    shouldShowError,
  } = useWorkData({ workId: workId ?? '' });

  useEffect(() => {
    if (isWorkFetching || !data) return;
    setWork(data, false);
    hasSetWork.current = true;
    setLockContext({ workId: data.id!, lockContext: data.activeLocks });
    if (!isEditingSteps) {
      setActiveTaskId(getWorkTaskId(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWorkFetching, isEditingSteps, data?.id]);

  const isWorkLoading =
    workQueryEnabled &&
    ((isSuccess && !hasSetWork.current) || _isWorkLoading || work?.id !== workId);
  const workErrorCode = getValueByPath({ json: workError, path: '$.response.status' });

  useEffect(() => {
    if (workId && selectedWorkId !== workId) {
      setSelectedWorkId(workId);
      setLastVisitedWorkId(workId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkId, workId]);

  useEffect(() => {
    return () => {
      setSelectedWorkId('');
      resetWorkPageStore();
      setRenderingMode(WorkLayoutRenderingMode.Feed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!flow) return;
    setFlow(flow);
    if (
      flow?.flowFeatures?.layout?.renderingMode &&
      renderingMode !== flow.flowFeatures.layout.renderingMode
    ) {
      setRenderingMode(flow.flowFeatures.layout.renderingMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow]);

  const isCorrectRenderingMode = flow?.flowFeatures?.layout?.renderingMode
    ? renderingMode === flow.flowFeatures.layout.renderingMode
    : true;

  const isLoading = isWorkLoading || !isCorrectRenderingMode;

  useWorkScroll({ isLoading });

  const rightDrawerProps = useWorkPageRightDrawerProps({ work, isLoading });
  const leftDrawerProps = useWorkPageLeftDrawerProps({ work, flow, isLoading });

  const onTaskChange = useCallback(
    (task: IStep) => {
      setActiveTaskId(task.id);
    },
    [setActiveTaskId],
  );

  const pageLayoutDrawerProps = !shouldShowError
    ? { leftDrawer: leftDrawerProps, rightDrawer: rightDrawerProps }
    : undefined;

  return (
    <PageLayout
      name={PageNames.WORK}
      title={work ? `${work?.displayName} (${work?.workCode})` : undefined}
      showOfflineError={false}
      headerWithBorder={!shouldShowError}
      {...pageLayoutDrawerProps}
      header={
        <WorkPageHeader
          displayName={work?.displayName}
          workCode={work?.workCode}
          workId={workId}
          flow={flow}
        />
      }
    >
      {shouldShowError ? (
        <ErrorView responseError={workError} />
      ) : (
        <WorkPageContent
          work={work}
          isLoading={isLoading}
          renderingMode={renderingMode}
          onTaskChange={onTaskChange}
          workErrorCode={workErrorCode}
        />
      )}
    </PageLayout>
  );
};
